import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { z } from 'zod'

import { InboxPage } from '@/components/emails/inbox/inbox-page'
import { DEFAULT_INBOX_TAB, INBOX_TABS, type InboxTab } from '@/components/emails/inbox/inbox-utils'
import { useHasEmailInboxFeature } from '@/hooks/use-has-feature'
import { usePermissions } from '@/hooks/use-permissions'
import { useCallback, useEffect } from 'react'
import { useStore } from '@tanstack/react-store'
import { organizationStore } from '@/stores/organization.store'

const INBOX_TAB_VALUES = INBOX_TABS.map((tab) => tab.value) as [InboxTab, ...InboxTab[]]

export const inboxSearchSchema = z.object({
  tab: z.enum(INBOX_TAB_VALUES).optional().catch(undefined),
  thread: z.string().optional().catch(undefined),
  mailbox: z.string().optional().catch(undefined),
  unread: z.boolean().optional().catch(undefined),
  q: z.string().optional().catch(undefined),
})

export type InboxSearch = z.infer<typeof inboxSearchSchema>

/**
 * The Inbox (CPRO-793): every conversation across the organization's
 * connected mailboxes. It needs the Connected inbox add-on and View
 * Communications; anyone else lands on the dashboard.
 */
export const Route = createFileRoute('/_app/_home/inbox')({
  component: InboxRoute,
  validateSearch: inboxSearchSchema,
})

function InboxRoute() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/_app/_home/inbox' })
  const organizationId = useStore(organizationStore, (state) => state.selectedOrganizationId) || ''
  const { hasFeature, hasLoaded } = useHasEmailInboxFeature()
  const { can, isLoading: isLoadingPermissions } = usePermissions(organizationId)
  const isReady = hasLoaded && !isLoadingPermissions && !!organizationId
  const isAllowed = hasFeature && can('CommsView')

  const onSearchChange = useCallback(
    (next: Partial<InboxSearch>) => navigate({ to: '/inbox', search: { ...search, ...next }, replace: true }),
    [navigate, search],
  )

  useEffect(() => {
    if (isReady && !isAllowed) {
      navigate({ to: '/dashboard', replace: true })
    }
  }, [isAllowed, isReady, navigate])

  if (!isReady || !isAllowed) {
    return <div className="flex min-h-[50dvh] items-center justify-center text-sm text-muted-foreground">Loading…</div>
  }

  return (
    <InboxPage
      organizationId={organizationId}
      search={{ ...search, tab: search.tab ?? DEFAULT_INBOX_TAB }}
      onSearchChange={onSearchChange}
    />
  )
}
